import {
  AIRecommendations,
  CmsUserSignature,
  ISdkService,
  SdkOpenExternalLinkParams,
  OpenGameOptions,
  UserData, GameRawData, ExternalBrandCallbackInfo, DateAndTimeFormat, ITokenInfo
} from './sdk.service.interface';
import {action, computed, observable} from 'mobx';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {
  DeviceInfo,
  GetAutoLoginTokenResponse,
  ICgpSdk,
  CgpSkinInfo,
  GetPlatformTokenResponse,
  ISdkUtils,
  ISdkRegulationService,
  IOpenHistoryGameRequest
} from './cgp-sdk.interface';
import {ServiceBase} from '../ServiceBase';
import {IRunningGameHandler} from '../games/running-game-handler.interface';
import {OpenLoginViewOptions} from '../user/user.service.interface';
import {SdkEvents} from './sdk-events';
import {ISdkComponentRenderer} from '../sdk-component-factory/sdk-component-factory.service.interface';
import {Check} from '@sparkware/app-utils';
import {IUsefulLinkSdkData} from "../user-menu/useful-links/useful-link-data.interface";
import {CGP_OBJECT_NAME} from '../../app-bootstrap/constants';
import {IAutoLoginParams} from '../../../common/interfaces/autologin-params.interface';
import { BalanceType } from '../../../common/const/wallet_balance_type';
import IStructuredLoggingService from "../../../common/interfaces/structured-logging-service-interface";
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {IGeoLocationInfo} from "../../../common/interfaces/geo-location-info.interface";
import {createDefaultCashierWidgetEligibility} from "../../../common/interfaces/cashier-widgets-eligibility-details.interface";
import {ICompanyViewModel} from "../companies/modules/company-view-model.interface";
import { IAbTestingFeatureProperties } from '../../../common/interfaces/ab-testing-interface';

export interface IGetAiRecommendationsOptions {
  maxGames?: number;
  filterOutRecentlyPlayedGames?: boolean;
  numberOfRecentlyPlayedGameToFilterOut?: number;
}

export class SdkService extends ServiceBase implements ISdkService {
  constructor(services: IServiceFactoryExtended) {
    super(services);

    this._updateLocalStorageAccordingToCookie();

    this._displayHybrid();

    this.reaction(() => this.services.location.currentUri,
      () => {
        if(this.services.location.hasNavigationHistory) {
          this.CGP.routeChanged();
        }
      });
  }

  private _displayHybrid() {
    //F TO DO why is this here and not in the SDK ?
    if(Check.isFunction(this.CGP.displayHybrid)) {
      this.CGP.displayHybrid();
    }
  }

  private _events: SdkEvents = null;

  get events(): SdkEvents {
    if(!this._events) {
      this._events = new SdkEvents(this.CGP);
    }

    return this._events;
  }



  private get CGP(): ICgpSdk {
    return this.services.window.getGlobalProperty(CGP_OBJECT_NAME) as ICgpSdk;
  }


  get  isAuthorized(): boolean {
    return this.CGP.isAuthorized();
  }

  get isLoginFromRegistration():boolean {
    return this.CGP.isLoginFromRegistration();
  }

  get getCGPMaxOverlayZIndex(): number {
    return this.CGP.getCGPMaxOverlayZIndex();
  }

  get structuredLoggingService(): IStructuredLoggingService {
    return this.CGP.structuredLoggingService();
  }

  get dateAndTimeFormat(): DateAndTimeFormat {
    return this.CGP.getRegulationDateAndTimeFormat();
  }

  get userData(): UserData {
    let rawUserData = this.CGP.getUserData();
    let rawAccountData = (rawUserData && rawUserData.accountData) || {};

    let depositWidgetEligibility = this.CGP.getDepositWidgetEligibilityDetails() || createDefaultCashierWidgetEligibility();
    let cashOutWidgetEligibility = this.CGP.getCashoutWidgetEligibilityDetails() || createDefaultCashierWidgetEligibility();

    return {
      userName: rawUserData?.userName,
      firstName: rawAccountData.FirstName,
      lastName: rawAccountData.LastName,
      IsVIP: this.CGP.isVIP(),
      DWHGameGroupOrientation: rawAccountData.DWHGameGroupOrientation, // when product are asking to do something with user Orientation, - it is not this one. use this.services.userGameOrientation.gameGroup
      statusGroupId: rawAccountData.StatusGroupId,
      GenderId: rawAccountData.genderID,
      depositWidgetEligibility: depositWidgetEligibility,
      cashOutWidgetEligibility: cashOutWidgetEligibility,
      timeSinceRegistration: rawAccountData.TimeSinceRegistration,
      cid: rawUserData?.cid,
      playerRMStatus: rawAccountData.PlayerRMStatus,
      sessionId: this.CGP.getSessionId(),
      registrationCountry: rawAccountData.RegistrationCountry,
      joinDate: rawAccountData.JoinDate,
      isFTD: rawAccountData.FTDSubBrand === this.getSubBrandID(),
      IsPromotionPermitted: rawAccountData.IsPromotionPermitted
    };
  }


  @observable isSdkReady: boolean = true;
  @action.bound
  private _setIsSdkReady(value: boolean) {
    this.isSdkReady = value;
  }

  getAIRecommendations(options?: IGetAiRecommendationsOptions): Promise<AIRecommendations> {
    return this.CGP.getAIList(options?.maxGames, options?.filterOutRecentlyPlayedGames, options?.numberOfRecentlyPlayedGameToFilterOut).then(data => {
      return {
        gameTypes: data.aiGameTypesArray || [],
        engine: data.aiEngine
      };
    });
  }

  getTrendingRecommendedGamesForPlayer(...listTypeIds: number[]):Promise<{[key:number] : number[]}> {
    return this.CGP.getTrendingRecommendedGamesForPlayer(listTypeIds);
  }

  getAINewRecommendations(options?: IGetAiRecommendationsOptions): Promise<AIRecommendations> {
    return this.CGP.getAINewGamesList(options?.maxGames, options?.filterOutRecentlyPlayedGames, options?.numberOfRecentlyPlayedGameToFilterOut).then(data => {
      return {
        gameTypes: data.aiGameTypesArray || [],
        engine: data.aiEngine
      };
    });
  }

  getAbTestingData():  { [key: string]: IAbTestingFeatureProperties } {
    return this.CGP.getAbTestingData();
  }

  getCurrentServerDate(): Date {
    return this.CGP.getCurrentServerDate();
  }

  getAbTestingDataForFeature(featureName: string): IAbTestingFeatureProperties {
    return this.CGP.getAbTestingDataForFeature(featureName);
  }

  sendAnalytics(category: string, action: string = null, label: string = null, value: string  = null, omitStructuredLogger: boolean = null) {
    try {
      this.CGP.sendAnalytics({
        category: category,
        action: action,
        label: label,
        value: value,
        omitStructuredLogger: omitStructuredLogger
      });
    } catch (ex) {
      this.services.logger.error(`SdkService failed to sendAnalytics: ${ex}`);
    }
  }

  getCmsUserSignature(): CmsUserSignature {
    return this.CGP.getUserSegments();
  }

  formatMoney(value: number, withDecimal: boolean = false): string {
    return this.CGP.format$$$(value, withDecimal);
  }

  get device(): DeviceInfo {
    return this.CGP.device();
  }

  waitForAutologin(): Promise<void> {
    return this.CGP.waitForAutologin();
  }

  openExternalLink(linkParams: SdkOpenExternalLinkParams, useCallbackURL = false): Promise<void> {

    let params: any = {...linkParams};

    //F TO DO - this if statement should be in SDK
    if(useCallbackURL && params.source === this.CGP.getSourceSubBrandName()) {
      params.callbackUrl = this.CGP.getExternalBrandCallbackURL();
    }

    return this.CGP.openExternalLink(params);
  }

  openResponsibleGamingLink(view?:number):void{
    this.CGP.openResponsibleGamingLink(view);
  }

  isGameRunning(): boolean{
    return this.CGP.isGameRunning();
  }

  async openGame(options: OpenGameOptions): Promise<IRunningGameHandler> {

    let openGameOptions: any = {...options};
    if(!options.showRegister) {
      openGameOptions.navbar = { //F TO DO - do we realy need this navbar option ?
        login: {
          showRegister: false
        }
      }
    }

   return await this.CGP.openGame(openGameOptions);

  }

  async openLoginForm(viewOptions: OpenLoginViewOptions = {}, marketing: MarketingParams = {}): Promise<void> {
    if (!viewOptions['marketingDataFn']) {
      marketing = marketing || {};
      viewOptions['marketingDataFn'] = 'getMarketingData(' + JSON.stringify(marketing) + ')';
    }

    await this.CGP.openLoginForm(viewOptions);
  }

  createComponentRenderer(componentId: string): ISdkComponentRenderer {
    return this.CGP.createComponentRenderer(componentId) as ISdkComponentRenderer;
  }

  getGamesInfoForHistory(gameTypes: number[]): Promise<GameRawData> {
    return this.CGP.getGamesInfoForHistory(gameTypes);
  }

  getGamesInfo(gameTypes: number[]): Promise<GameRawData> {
    return this.CGP.getGamesInfo(gameTypes);
  }

  getAutoLoginToken(): Promise<GetAutoLoginTokenResponse> {
    return this.CGP.getAutoLoginToken();
  }

  getPlatformToken(): Promise<GetPlatformTokenResponse> {
    return this.CGP.getPlatformToken();
  }

  getCorrelatedGames(gameType: number, numberOfGames: number = 3): Promise<number[]> {
    return this.CGP.getCorrelatedGames(gameType, numberOfGames);
  }

  logout(): Promise<void> {
    return this.CGP.logout();
  }

  getBrandID(): number {
    return this.CGP.getBrandID();
  }

  getSubBrandID(): number {
    return this.CGP.getSubBrandID();
  }

  getRegulationService(): ISdkRegulationService {
    return this.CGP.getRegulationService();
  }

  isHybridEmbeddedGame(gameType: number): boolean {
    return this.CGP.isHybridEmbeddedGame(gameType);
  }

  openHybridLink(url: string): void {
    this.CGP.openHybridLink(url);
  }

  private _updateLocalStorageAccordingToCookie() {
    try {
      let autologinCookie = this.services.storage.getCookie('orbit_autologin_token');

      if(autologinCookie) {
        this.CGP.setAutoLoginToken(autologinCookie);
      }
    } catch (err) {
      this.services.logger.error('update local storage according to cookie failed. Error: ' + err);
    } finally {
      // Remove cookie
      this.services.storage.removeCookie('orbit_autologin_token');
    }

  }

  getGRSEnvironmentUrls(): Promise<any> {
    return this.CGP.getGRSEnvironments();
  }

  @computed get activeChallengeCount(): number {
    return this.CGP.countPlayerChallenges();
  }

  openInNewWindow(url: string, features?: string): void {
    return this.CGP.openInNewWindow(url, features);
  }

  executeMenuAction(id: string): void {
    this.CGP.menuActionExecute(id);
  }

  closeRunningGame(): void {
    this.CGP.closeRunningGame();
  }

  getExternalBrandCallbackInfo(): ExternalBrandCallbackInfo {
    return {
      method: this.CGP.getExternalBrandCallbackURLMethod(),
      url: this.CGP.getExternalBrandCallbackURL()
    }
  }

  menuActionExecute(key: string): void {
    return this.CGP.menuActionExecute(key);
  }

  async getUsefulLinks(): Promise<IUsefulLinkSdkData[]> {
    try {
      return await this.CGP.menuJSON();
    } catch(ex) {
      console.error("Failed to get CGP menuJSON!", ex);
      return [];
    }
  }

  startLiveTableFeeds(): void {
    if(Check.isFunction(this.CGP.startLiveTableFeeds)) {
      this.CGP.startLiveTableFeeds();
    } else {
      //TODO - to be removed in the future
      (this.CGP as any).startEvolutionLiveFeeds();
    }
  }

  stopLiveTableFeeds(): void {
    if(Check.isFunction(this.CGP.stopLiveTableFeeds)) {
      this.CGP.stopLiveTableFeeds();
    } else {
      //TODO - to be removed in the future
      (this.CGP as any).stopEvolutionLiveFeeds();
    }
  }

  refreshThirdPartyVouchers(): void {
    this.CGP.refreshThirdPartyVouchers();
  }

  closeCurrentDetailHistory(): void {
    // we check if closeCurrentDetailHistory is defined just in case Orbit is not deployed along with the SDK version that contains this function
    if(this.CGP.closeCurrentDetailHistory) {
        this.CGP.closeCurrentDetailHistory();
    }
  }

  getRichPageUrl(campaignId: string) {
    return this.CGP.getRichPageUrl(campaignId);
  }

  getDailyDealCampaigns() {
    return this.CGP.getDailyDealCampaigns();
  }

  getJackpotsWithState() {
    return this.CGP.getJackpotsWithState();
  }

  getJackpotAmountByGameType(gameType: string) {
    return this.CGP.getJackpotAmountByGameType(gameType);
  }

  getNumberOfAwaitingMessages() {
    return this.CGP.getNumberOfAwaitingMessages();
  }

  openOmgAwaitingMessages() {
    return this.CGP.openOmgAwaitingMessages();
  }

  getFreeplayVouchers() {
    return this.CGP.getFreeplayVouchers();
  }

  showToaster(toasterData){
    return this.CGP.showToaster(toasterData);
  }

  getBankroll(balanceType: BalanceType, plainNumber?: boolean) {
    return this.CGP.getBankroll(balanceType, plainNumber);
  }

  runAutoLogin(autoLoginParams: IAutoLoginParams) {
    return this.CGP.runAutoLogin(autoLoginParams);
  }

  loadSafezoneSdk():Promise<void>{
    return this.CGP.loadSafezoneSdk();
  }

  getProductPackageId(): number{
    return this.CGP.getProductPackage();
  }

  getSkin(): CgpSkinInfo {
    return {
      getPlatformSystemID: () => this.CGP.getSkin().getPlatformSystemID(),
      getClientPlatform: () => this.CGP.getSkin().getClientPlatform(),
      getVersion: () => this.CGP.getSkin().getVersion(),
      getCulture: () => this.CGP.getSkin().getCulture(),
      shouldReplaceLoginWithDownloadAppForMobileWebApp: () => this.CGP.getSkin().shouldReplaceLoginWithDownloadAppForMobileWebApp(),
      shouldPreventOpenGameForMobileWebApp: () => this.CGP.getSkin().shouldPreventOpenGameForMobileWebApp(),
      getBrandName: () => this.CGP.getSkin().getBrandName(),
    }
  }

  getUtils(): ISdkUtils {
    return this.CGP.getUtils();
  };

  fireOrbitInitComplete(): void {
    this.CGP.onOrbitInitComplete();
  }

  getEnvironment():string{
    return this.CGP.getEnvironment();
  }

  pushDataToDataLayer(data: {}):void{
    this.CGP.pushDataToDataLayer(data);
  }

  getCountryName() {
    return this.CGP.getCountryName();
  }

  getCountryIso3(): string {
    return this.CGP.getCountryIso3();
  }


  httpPost<TResponse>(url: string, requestData: unknown): Promise<TResponse> {
    return this.CGP.httpPost<TResponse>(url, requestData);
  }
  httpGet<TResponse>(url: string, requestData: unknown): Promise<TResponse> {
    return this.CGP.httpGet<TResponse>(url, requestData);
  }
  getConfigs(): any {
    return this.CGP.getConfigs();
  }

  loadScript(url: string, tagId?: string): Promise<void> {
    return this.CGP.loadScript(url, tagId);
  }

  getRefreshToken(): string {
    return this.CGP.getRefreshToken();
  }

  getCurrencyIso3(): string {
    return this.CGP.getCurrencyIso3();
  }

  get overlayZIndex() {
    return this.CGP.overlayZIndex;
  }

  getGoogleClientID(): string {
    return this.CGP.getGoogleClientID();
  }

  isInAutologin(): boolean {
    return this.CGP.isInAutologin();
  }

  fireDepositWidgetClosed(): void {
    this.CGP.fireDepositWidgetClosed();
  }

  get tokenInfo(): ITokenInfo | null {
    return this.CGP.getToken() || null;
  }

  redirectToHistory(): void {
    return this.CGP.openHistoryLink();
  }

  continuePlayingCurrentGame(): void {
    return this.CGP.continuePlayingCurrentGame();
  }

  getGeoLocationInfo(): Promise<IGeoLocationInfo | null> {
    return this.CGP.getGeoLocationInfo();
  }

  getDownloadAppLink(): string {
    return this.CGP.getDownloadAppLink();
  }

  isDepositWidgetSupportedByHybrid(): boolean {
    return this.CGP.isDepositWidgetSupportedByHybrid();
  }

  promptPushNotificationPermissionRequest(){
    return this.CGP.promptPushNotificationPermissionRequest();
  }

  openPopUpOnSecondWebView(content: string, timeout : number) : void {
    this.CGP.openPopUpOnSecondWebView(content, timeout);
  }

  closePopUpOnSecondWebView() : void {
    this.CGP.closePopUpOnSecondWebView();
  }

  bringOmgToFront(): void {
    this.CGP.bringOmgToFront();
  }

  getBurnoutAnimationZIndex() {
    return this.CGP.getBurnoutAnimationZIndex();
  }

  getCompanyByName(name: string): Promise<ICompanyViewModel> {
    return this.CGP.getCompanyByName(name);
  }

  openHistoryGame(request: IOpenHistoryGameRequest): Promise<void> {
    return this.CGP.openHistoryGame(request);
  }
}
